import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { bookingFaqSnippets, faqAnchorId, type FaqItem } from "@/data/faqs";
import { trackEvent } from "@/lib/analytics";

type Props = {
  title?: string;
  items?: FaqItem[];
  className?: string;
  /** Where this snippet block is rendered — used for analytics attribution. */
  source?: string;
};

/**
 * Compact FAQ block for booking/checkout pages.
 * Answers common drop-off questions inline before users abandon.
 */
const FaqSnippets = ({
  title = "QUICK ANSWERS BEFORE YOU BOOK",
  items = bookingFaqSnippets,
  className = "",
  source = "booking",
}: Props) => (
  <aside
    aria-label="Frequently asked questions about booking"
    className={`bg-panel border border-border p-5 md:p-6 ${className}`}
  >
    <div className="flex items-center gap-2 mb-4">
      <HelpCircle className="w-4 h-4 text-primary" />
      <h3 className="font-display tracking-widest text-xs md:text-sm">{title}</h3>
    </div>
    <Accordion
      type="single"
      collapsible
      className="w-full"
      onValueChange={(value) => {
        if (!value) return;
        const idx = Number(value.replace(/^faq-snippet-/, ""));
        const item = items[idx];
        if (!item) return;
        trackEvent("faq_question_open", {
          question: item.q,
          category: item.category,
          source,
        });
      }}
    >
      {items.map((f, i) => (
        <AccordionItem
          key={f.q}
          value={`faq-snippet-${i}`}
          className="border-border"
        >
          <AccordionTrigger className="text-left font-body text-sm hover:no-underline">
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="text-sm text-foreground/80 font-body">
            <p>{f.a}</p>
            <Link
              to={`/faq#${faqAnchorId(f.q)}`}
              onClick={() =>
                trackEvent("faq_deeplink_click", {
                  question: f.q,
                  category: f.category,
                  source,
                })
              }
              className="inline-block mt-2 text-[11px] font-mono uppercase tracking-widest text-primary hover:underline"
            >
              More on this →
            </Link>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
    <div className="mt-4 text-xs font-mono uppercase tracking-widest">
      <Link
        to="/faq"
        onClick={() => trackEvent("faq_see_all_click", { source })}
        className="text-primary hover:underline"
      >
        See full FAQ →
      </Link>
    </div>
  </aside>
);

export default FaqSnippets;